/**
 * Email Template Editor Panel
 *
 * Full-featured editor for Super Admin email template overrides with:
 * - Monaco HTML editor (lazy-loaded) with syntax highlighting
 * - Live iframe preview (debounced, uses sample data)
 * - Variable picker with click-to-insert at Monaco cursor
 * - Subject line + preheader inputs
 * - Reset to default, test-send, and save actions
 */
import { useState, useEffect, useRef, useCallback, Suspense, lazy, useDeferredValue } from 'react';
import { cn, Button, Input, Label, Separator } from '@respectly/ui';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@respectly/ui';
import {
  Mail,
  RotateCcw,
  Send,
  Save,
  Loader2,
  Eye,
  Code2,
  ChevronDown,
  ChevronUp,
  User,
  FileText,
  CreditCard,
  Headphones,
  Users,
  Cpu,
  CheckCircle2,
  AlertTriangle,
  Search,
  X,
} from 'lucide-react';
import type { EmailTemplateDefinition, EmailTemplateVariable, EmailTemplateKey } from '@respectly/shared-types';
import { ALL_EMAIL_TEMPLATE_VARIABLES } from '@respectly/shared-types';
import type { editor as MonacoEditorNS } from 'monaco-editor';

// Lazy-load Monaco editor to avoid bundle bloat (only loaded when editor opens)
const MonacoEditor = lazy(() => import('@monaco-editor/react'));

// ============================================
// Variable group config
// ============================================

const GROUP_CONFIG: Record<string, { label: string; icon: React.ElementType; accentClass: string } | undefined> = {
  contact: { label: 'Contact', icon: User, accentClass: 'text-blue-600 dark:text-blue-400' },
  document: { label: 'Document', icon: FileText, accentClass: 'text-violet-600 dark:text-violet-400' },
  billing: { label: 'Billing', icon: CreditCard, accentClass: 'text-amber-600 dark:text-amber-400' },
  support: { label: 'Support', icon: Headphones, accentClass: 'text-rose-600 dark:text-rose-400' },
  team: { label: 'Organization', icon: Users, accentClass: 'text-emerald-600 dark:text-emerald-400' },
  system: { label: 'System', icon: Cpu, accentClass: 'text-slate-500 dark:text-slate-400' },
};

// ============================================
// Variable Group Component
// ============================================

interface VariableGroupProps {
  group: string;
  variables: EmailTemplateVariable[];
  nativeKeys: Set<string>;
  onInsert: (placeholder: string) => void;
}

function VariableGroup({ group, variables, nativeKeys, onInsert }: VariableGroupProps) {
  const [collapsed, setCollapsed] = useState(false);
  const config = GROUP_CONFIG[group] ?? GROUP_CONFIG['system']!;
  const Icon = config.icon;

  if (variables.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center gap-1.5 w-full group"
      >
        <Icon className={cn('w-3 h-3 shrink-0', config.accentClass)} />
        <span className={cn('text-[11px] font-semibold uppercase tracking-wider', config.accentClass)}>
          {config.label}
        </span>
        <span className="ml-1.5 text-[10px] text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal">
          ({variables.length})
        </span>
        {collapsed ? (
          <ChevronDown className="w-3 h-3 ml-auto text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
        ) : (
          <ChevronUp className="w-3 h-3 ml-auto text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
        )}
      </button>
      {!collapsed && (
        <div className="flex flex-wrap gap-1.5 pl-4">
          {variables.map((v) => {
            const isNative = nativeKeys.has(v.key);
            return (
              <button
                key={v.key}
                type="button"
                title={`${v.description}${isNative ? '' : ' (available from other templates)'}`}
                onClick={() => onInsert(v.placeholder)}
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono',
                  'border transition-all duration-150',
                  'hover:scale-105 hover:shadow-sm active:scale-95',
                  isNative
                    ? 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                    : 'bg-white dark:bg-slate-900/40 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500',
                  config.accentClass.includes('blue') && 'hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-700 dark:hover:text-blue-300',
                  config.accentClass.includes('violet') && 'hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 dark:hover:bg-violet-900/20 dark:hover:border-violet-700 dark:hover:text-violet-300',
                  config.accentClass.includes('amber') && 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 dark:hover:bg-amber-900/20 dark:hover:border-amber-700 dark:hover:text-amber-300',
                  config.accentClass.includes('rose') && 'hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 dark:hover:bg-rose-900/20 dark:hover:border-rose-700 dark:hover:text-rose-300',
                  config.accentClass.includes('emerald') && 'hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:border-emerald-700 dark:hover:text-emerald-300',
                )}
              >
                {v.placeholder}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// Variable resolver (client-side preview only)
// ============================================

function resolvePreview(html: string, vars: Record<string, string>): string {
  return html.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}

function getSampleData(variables: EmailTemplateVariable[]): Record<string, string> {
  const data: Record<string, string> = {};
  for (const v of variables) {
    data[v.key] = v.sampleValue;
  }
  return data;
}

// ============================================
// Test Send Dialog
// ============================================

interface TestSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (to: string) => Promise<void>;
  isSending: boolean;
}

function TestSendDialog({ open, onOpenChange, onSend, isSending }: TestSendDialogProps) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setSent(false);
    }
  }, [open]);

  const handleSend = async () => {
    if (!email.trim()) return;
    await onSend(email.trim());
    setSent(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-500" />
            Send Test Email
          </DialogTitle>
          <DialogDescription>
            Sends this template with sample data to the address below.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
              Test email sent to <strong>{email}</strong>
            </p>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="test-send-email">Recipient Email</Label>
              <Input
                id="test-send-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {sent ? 'Close' : 'Cancel'}
          </Button>
          {!sent && (
            <Button onClick={handleSend} disabled={!email.trim() || isSending}>
              {isSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// Props
// ============================================

export interface EmailTemplateEditorPanelProps {
  templateKey: EmailTemplateKey;
  definition: EmailTemplateDefinition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSubject: string;
  initialHtml: string;
  initialPreheader: string | null;
  isCustomized: boolean;
  onSave: (subject: string, htmlContent: string, preheader: string | undefined) => Promise<boolean>;
  onReset: () => Promise<boolean>;
  onTestSend: (to: string) => Promise<boolean>;
  isSaving: boolean;
  isResetting: boolean;
  isSendingTest: boolean;
}

// ============================================
// Component
// ============================================

export function EmailTemplateEditorPanel({
  templateKey,
  definition,
  open,
  onOpenChange,
  initialSubject,
  initialHtml,
  initialPreheader,
  isCustomized,
  onSave,
  onReset,
  onTestSend,
  isSaving,
  isResetting,
  isSendingTest,
}: EmailTemplateEditorPanelProps) {
  const editorRef = useRef<MonacoEditorNS.IStandaloneCodeEditor | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [draftSubject, setDraftSubject] = useState(initialSubject);
  const [draftHtml, setDraftHtml] = useState(initialHtml);
  const [draftPreheader, setDraftPreheader] = useState(initialPreheader ?? '');
  const [showCode, setShowCode] = useState(false);
  const [showTestSend, setShowTestSend] = useState(false);
  const [varSearch, setVarSearch] = useState('');

  // Compute dirty state
  const isDirty =
    draftSubject !== initialSubject ||
    draftHtml !== initialHtml ||
    draftPreheader !== (initialPreheader ?? '');

  // Debounced HTML for preview (avoid re-rendering on every keystroke)
  const deferredHtml = useDeferredValue(draftHtml);
  const sampleData = getSampleData(ALL_EMAIL_TEMPLATE_VARIABLES);
  const previewHtml = resolvePreview(deferredHtml, sampleData);

  // Imperatively write HTML to the iframe so it reliably updates on every change
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(previewHtml);
        doc.close();
      }
    } catch {
      // cross-origin safety in case the iframe src is changed externally
    }
  }, [previewHtml]);

  // Keys native to this specific template — used for visual distinction
  const nativeKeys = new Set(definition.variables.map((v) => v.key));

  // Filter all variables by search term, then group by their group field.
  // varSearch.toLowerCase() is computed once to avoid repeated calls per variable.
  const lowerSearch = varSearch.trim().toLowerCase();
  const filteredVars = lowerSearch
    ? ALL_EMAIL_TEMPLATE_VARIABLES.filter(
        (v) =>
          v.key.toLowerCase().includes(lowerSearch) ||
          v.label.toLowerCase().includes(lowerSearch) ||
          v.description.toLowerCase().includes(lowerSearch)
      )
    : ALL_EMAIL_TEMPLATE_VARIABLES;

  const groupedVars = filteredVars.reduce<Record<string, EmailTemplateVariable[]>>(
    (acc, v) => {
      if (!acc[v.group]) acc[v.group] = [] as EmailTemplateVariable[];
      acc[v.group]!.push(v);
      return acc;
    },
    {}
  );

  // Sync drafts when template is loaded/reset
  useEffect(() => {
    setDraftSubject(initialSubject);
    setDraftHtml(initialHtml);
    setDraftPreheader(initialPreheader ?? '');
    setShowCode(false);
    setVarSearch('');
  }, [initialSubject, initialHtml, initialPreheader]);

  // Insert variable at Monaco cursor position
  const handleInsertVariable = useCallback((placeholder: string) => {
    const editor = editorRef.current;
    if (!editor) {
      // Fallback: append to end
      setDraftHtml((prev) => prev + placeholder);
      return;
    }
    const selection = editor.getSelection();
    if (selection) {
      editor.executeEdits('insert-variable', [
        { range: selection, text: placeholder, forceMoveMarkers: true },
      ]);
    }
    editor.focus();
  }, []);

  const handleSave = async () => {
    await onSave(draftSubject, draftHtml, draftPreheader || undefined);
  };

  const handleReset = async () => {
    if (!window.confirm(
      `Reset "${definition.name}" to the system default? Your customizations will be permanently deleted.`
    )) return;
    await onReset();
  };

  const handleTestSend = async (to: string) => {
    await onTestSend(to);
  };

  const categoryColors: Record<string, string> = {
    auth: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    billing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    team: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    documents: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    support: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl flex flex-col p-0 overflow-hidden"
        >
          {/* Header */}
          <SheetHeader className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 flex items-center justify-center shrink-0 mt-0.5">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <SheetTitle className="text-base">{definition.name}</SheetTitle>
                  <span className={cn('text-[11px] font-medium px-2 py-0.5 rounded-full capitalize', categoryColors[definition.category] ?? categoryColors.auth)}>
                    {definition.category}
                  </span>
                  {isCustomized ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Customized
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      Default
                    </span>
                  )}
                </div>
                <SheetDescription className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                  {definition.description}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-5">
              {/* Subject line */}
              <div className="space-y-1.5">
                <Label htmlFor={`${templateKey}-subject`}>Subject Line</Label>
                <Input
                  id={`${templateKey}-subject`}
                  value={draftSubject}
                  onChange={(e) => setDraftSubject(e.target.value)}
                  placeholder="Email subject..."
                />
              </div>

              {/* Preheader */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor={`${templateKey}-preheader`}>Preheader</Label>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">optional</span>
                </div>
                <Input
                  id={`${templateKey}-preheader`}
                  value={draftPreheader}
                  onChange={(e) => setDraftPreheader(e.target.value)}
                  placeholder="Preview text shown in email clients..."
                />
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  Short summary shown next to the subject in inbox previews (under 200 characters).
                </p>
              </div>

              <Separator />

              {/* Variable Picker */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Insert Variable
                  </Label>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    Click to insert at cursor
                  </span>
                </div>

                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={varSearch}
                    onChange={(e) => setVarSearch(e.target.value)}
                    placeholder="Search variables…"
                    className={cn(
                      'w-full pl-8 pr-8 py-1.5 text-xs rounded-md',
                      'border border-slate-200 dark:border-slate-700',
                      'bg-white dark:bg-slate-900',
                      'text-slate-800 dark:text-slate-200',
                      'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                      'focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400',
                    )}
                  />
                  {varSearch && (
                    <button
                      type="button"
                      onClick={() => setVarSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-3 space-y-3">
                  {Object.entries(groupedVars).length === 0 ? (
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center py-2">
                      No variables match your search.
                    </p>
                  ) : (
                    Object.entries(groupedVars).map(([group, vars], idx) => (
                      <div key={group}>
                        {idx > 0 && <div className="border-t border-slate-200 dark:border-slate-700/60 mb-3" />}
                        <VariableGroup
                          group={group}
                          variables={vars}
                          nativeKeys={nativeKeys}
                          onInsert={handleInsertVariable}
                        />
                      </div>
                    ))
                  )}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-3 text-[10px] text-slate-400 dark:text-slate-500 px-0.5">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-10 h-4 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60" />
                    Native to this template
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-10 h-4 rounded border border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40" />
                    From other templates
                  </span>
                </div>
              </div>

              <Separator />

              {/* Live Preview (always visible) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5 text-teal-500" />
                    Live Preview (sample data)
                  </Label>
                </div>
                <div className="rounded-lg border border-teal-200/60 dark:border-teal-800/40 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-teal-200/60 dark:border-teal-800/40 bg-teal-50 dark:bg-teal-900/20">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    <span className="text-[11px] font-medium text-teal-700 dark:text-teal-400 uppercase tracking-wider">
                      Rendered with sample data
                    </span>
                  </div>
                  {/* iframe rendered imperatively via useEffect — no srcDoc to avoid stale renders */}
                  <iframe
                    ref={iframeRef}
                    title="Email Preview"
                    className="w-full h-[500px] bg-white"
                  />
                </div>
              </div>

              {/* HTML Editor (collapsible, hidden by default) */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowCode((c) => !c)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-medium transition-colors',
                    showCode
                      ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
                      : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <Code2 className="w-3.5 h-3.5" />
                    {showCode ? 'Hide HTML Code' : 'Show HTML Code'}
                  </span>
                  {showCode ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>

                {showCode && (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <Suspense
                      fallback={
                        <div className="h-[400px] flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      }
                    >
                      <MonacoEditor
                        height="400px"
                        language="html"
                        theme="vs-dark"
                        value={draftHtml}
                        onChange={(value) => setDraftHtml(value ?? '')}
                        onMount={(editor) => {
                          editorRef.current = editor;
                        }}
                        options={{
                          minimap: { enabled: false },
                          lineNumbers: 'on',
                          wordWrap: 'on',
                          fontSize: 13,
                          tabSize: 2,
                          scrollBeyondLastLine: false,
                          padding: { top: 12, bottom: 12 },
                          folding: true,
                          autoClosingBrackets: 'always',
                          formatOnPaste: true,
                        }}
                      />
                    </Suspense>
                  </div>
                )}
              </div>

              {/* Warning when dirty */}
              {isDirty && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    You have unsaved changes. Click <strong>Save Changes</strong> to apply them.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sticky action bar */}
          <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
            <div className="flex items-center gap-2">
              {/* Reset to default */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isResetting || !isCustomized}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800 disabled:opacity-40"
              >
                {isResetting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                )}
                Reset to Default
              </Button>

              <div className="flex-1" />

              {/* Test send */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTestSend(true)}
                disabled={isSendingTest}
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send Test
              </Button>

              {/* Save */}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !isDirty}
              >
                {isSaving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Test Send Dialog */}
      <TestSendDialog
        open={showTestSend}
        onOpenChange={setShowTestSend}
        onSend={handleTestSend}
        isSending={isSendingTest}
      />
    </>
  );
}
